import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Fade,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import {
  Assignment,
  BugReport,
  CheckCircle,
  CloudUpload,
  Delete,
  Devices,
  ErrorOutline,
  Groups,
  Insights,
  Person,
  Refresh,
  SystemUpdateAlt,
  TaskAlt,
  WarningAmber,
  DoneAll,
} from '@mui/icons-material'
import { useStore } from '../store/authStore'
import { customerService, dashboardService, errorLogService, systemUpdateService } from '../services/api'
import {
  CreateSystemReleasePayload,
  CustomerRecord,
  DashboardErrorLog,
  DashboardRecentVisit,
  DashboardStats,
  DashboardSubscriptionAlert,
  ReleaseEnrollmentBoard,
  SystemUpdateInfo,
} from '../types'

const PACKAGE_SLOTS = [
  { product: 'WinApp', label: 'تنصيبي الحاسبة (WinApp)', accept: '.exe,.zip,.msi' },
  { product: 'Api', label: 'تنصيبي الـ API', accept: '.exe,.zip,.msi' },
  { product: 'Mobile', label: 'APK المندوبين (MeraRoute)', accept: '.apk,.zip' },
  { product: 'MeraApp', label: 'APK ميرا (MeraApp)', accept: '.apk,.zip' },
] as const

type StatCardProps = {
  title: string
  value: number
  subtitle: string
  icon: React.ReactElement
  color: string
  onClick?: () => void
}

const glassCardSx = {
  borderRadius: 4,
  background: 'linear-gradient(135deg, rgba(255,255,255,0.96), rgba(255,255,255,0.88))',
  backdropFilter: 'blur(14px)',
  border: '1px solid rgba(255,255,255,0.35)',
  boxShadow: '0 12px 36px rgba(15, 23, 42, 0.08)',
}

const formatDateTime = (value?: string | null) => {
  if (!value) return 'غير متوفر'
  return new Date(value).toLocaleString('ar-IQ', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const getVisitTypeLabel = (visitType: string) => {
  switch (visitType) {
    case 'Activation':
      return 'تفعيل'
    case 'Update':
      return 'تحديث'
    case 'Maintenance':
      return 'صيانة'
    case 'PrinterInstall':
      return 'تنصيب طابعة'
    default:
      return visitType
  }
}

const getErrorSourceColor = (source: string) => {
  switch (source.toLowerCase()) {
    case 'api':
      return 'error'
    case 'web':
      return 'info'
    case 'mobile':
      return 'secondary'
    case 'desktop':
      return 'warning'
    default:
      return 'default'
  }
}

const getSubscriptionStateLabel = (state: string) => {
  switch (state) {
    case 'PendingActivation':
      return 'بانتظار التفعيل'
    case 'Active':
      return 'فعال'
    case 'ExpiringSoon':
      return 'قارب على الانتهاء'
    case 'GracePeriod':
      return 'فترة سماح'
    case 'Expired':
      return 'منتهي'
    case 'Permanent':
      return 'دائمي'
    default:
      return state
  }
}

const getSubscriptionStateColor = (state: string) => {
  switch (state) {
    case 'Active':
    case 'Permanent':
      return 'success'
    case 'ExpiringSoon':
    case 'PendingActivation':
      return 'warning'
    case 'GracePeriod':
      return 'info'
    case 'Expired':
      return 'error'
    default:
      return 'default'
  }
}

const getSubscriptionTypeLabel = (type: string) => {
  switch (type) {
    case 'Trial':
      return 'تجريبي'
    case 'Monthly':
      return 'شهري'
    case 'Yearly':
      return 'سنوي'
    case 'Permanent':
      return 'دائمي'
    default:
      return type
  }
}

const StatCard = ({ title, value, subtitle, icon, color, onClick }: StatCardProps) => (
  <Card
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={
      onClick
        ? (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              onClick()
            }
          }
        : undefined
    }
    sx={{
      ...glassCardSx,
      height: '100%',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      '&:hover': onClick
        ? {
            transform: 'translateY(-4px)',
            boxShadow: '0 18px 40px rgba(15, 23, 42, 0.12)',
            borderColor: alpha(color, 0.45),
          }
        : undefined,
    }}
  >
    <CardContent sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.75 }}>
            {value.toLocaleString('ar-IQ')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {subtitle}
          </Typography>
        </Box>
        <Avatar
          variant="rounded"
          sx={{
            width: 52,
            height: 52,
            borderRadius: 3,
            backgroundColor: alpha(color, 0.12),
            color,
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: 28 } })}
        </Avatar>
      </Stack>
    </CardContent>
  </Card>
)

export default function DashboardPage() {
  const navigate = useNavigate()
  const { user } = useStore()
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [errorActionId, setErrorActionId] = useState<number | null>(null)
  const [releaseSaving, setReleaseSaving] = useState(false)
  const [releaseDeletingId, setReleaseDeletingId] = useState<string | null>(null)
  const [releaseFeedback, setReleaseFeedback] = useState<string | null>(null)
  const [releases, setReleases] = useState<SystemUpdateInfo[]>([])
  const [releaseForm, setReleaseForm] = useState<CreateSystemReleasePayload>({
    version: '1.0.1',
    title: '',
    notes: '',
    isMandatory: true,
    product: 'Suite',
    targetScope: 'All',
    targetIds: [],
    forceSameVersion: false,
  })
  const [selectedCustomers, setSelectedCustomers] = useState<CustomerRecord[]>([])
  const [customerOptions, setCustomerOptions] = useState<CustomerRecord[]>([])
  const [packageFiles, setPackageFiles] = useState<Record<string, File | null>>({
    WinApp: null,
    Api: null,
    Mobile: null,
    MeraApp: null,
  })
  const [uploadPercent, setUploadPercent] = useState<number | null>(null)
  const [boardOpen, setBoardOpen] = useState(false)
  const [boardRelease, setBoardRelease] = useState<SystemUpdateInfo | null>(null)
  const [board, setBoard] = useState<ReleaseEnrollmentBoard | null>(null)
  const [boardLoading, setBoardLoading] = useState(false)
  const [addCustomerPick, setAddCustomerPick] = useState<CustomerRecord | null>(null)
  const [boardQuery, setBoardQuery] = useState('')

  const fetchDashboardStats = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await dashboardService.getStats()
      const payload = response?.data ?? response?.Data ?? null

      if (!payload) {
        throw new Error('تعذر قراءة بيانات لوحة التحكم من الخادم')
      }

      setStats(payload as DashboardStats)
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'فشل تحميل بيانات لوحة التحكم')
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchReleases = useCallback(async () => {
    if (user?.role !== 'Owner') {
      setReleases([])
      return
    }

    try {
      const response = await systemUpdateService.getAll()
      const payload = response?.data ?? response?.Data ?? response
      setReleases(Array.isArray(payload) ? payload : [])
    } catch {
      setReleases([])
    }
  }, [user?.role])

  useEffect(() => {
    fetchDashboardStats()
  }, [fetchDashboardStats])

  useEffect(() => {
    fetchReleases()
  }, [fetchReleases])

  useEffect(() => {
    if (user?.role !== 'Owner') return
    ;(async () => {
      try {
        const response = await customerService.getAll()
        const payload = response?.data ?? response?.Data ?? response
        setCustomerOptions(Array.isArray(payload) ? payload : [])
      } catch {
        setCustomerOptions([])
      }
    })()
  }, [user?.role])

  const loadBoard = async (release: SystemUpdateInfo) => {
    setBoardRelease(release)
    setBoardOpen(true)
    setBoardLoading(true)
    setBoard(null)
    setBoardQuery('')
    try {
      const response = await systemUpdateService.getEnrollmentBoard(release.id)
      const payload = response?.data ?? response?.Data ?? response
      setBoard(payload as ReleaseEnrollmentBoard)
    } catch (err: any) {
      setReleaseFeedback(err?.response?.data?.message || 'تعذر جلب حالة الأجهزة')
    } finally {
      setBoardLoading(false)
    }
  }

  const boardDeviceTypeLabel = (type?: string) => {
    switch (type) {
      case 'Main':
        return 'رئيسية'
      case 'Sub':
        return 'فرعية'
      case 'Api':
        return 'API'
      case 'Mobile':
        return 'Route'
      case 'MeraApp':
        return 'ميرا موبايل'
      default:
        return type || '—'
    }
  }

  const filteredBoardDevices = useMemo(() => {
    const rows = board?.devices ?? []
    const q = boardQuery.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((row) => {
      const hay = [
        row.customerName,
        row.deviceCode,
        row.computerName,
        row.deviceType,
        row.lastUpdatedByName,
        row.markedUpdatedByName,
        row.activationCode,
        row.notes,
        row.mainActivationCode,
        row.mainComputerName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return hay.includes(q)
    })
  }, [board, boardQuery])

  const handleFixError = async (errorId: number) => {
    try {
      setErrorActionId(errorId)
      await errorLogService.markFixed(errorId, {})
      await fetchDashboardStats()
    } catch (err: any) {
      setError(err?.response?.data?.message || 'تعذر تعليم الخطأ كمحلول')
    } finally {
      setErrorActionId(null)
    }
  }

  const handleReportError = async (errorId: number) => {
    try {
      setErrorActionId(errorId)
      await errorLogService.markReported(errorId, {})
      await fetchDashboardStats()
    } catch (err: any) {
      setError(err?.response?.data?.message || 'تعذر تعليم الخطأ كمبلغ')
    } finally {
      setErrorActionId(null)
    }
  }

  const handleCreateRelease = async () => {
    if (!releaseForm.version.trim() || !releaseForm.title.trim()) {
      setReleaseFeedback('رقم الإصدار وعنوان التحديث مطلوبان')
      return
    }

    const scope = releaseForm.targetScope || 'All'
    const ids = selectedCustomers.map((c) => c.id).filter(Boolean)
    if (scope === 'Customers' && ids.length === 0) {
      setReleaseFeedback('اختر عميلاً واحداً على الأقل من القائمة')
      return
    }

    const filesToUpload = PACKAGE_SLOTS.map((s) => ({
      product: s.product,
      file: packageFiles[s.product],
    })).filter((x) => !!x.file) as { product: string; file: File }[]

    try {
      setReleaseSaving(true)
      setReleaseFeedback(null)
      setUploadPercent(null)
      const created = await systemUpdateService.createRelease({
        version: releaseForm.version.trim(),
        title: releaseForm.title.trim(),
        notes: releaseForm.notes?.trim() || undefined,
        isMandatory: releaseForm.isMandatory,
        product: releaseForm.product || 'Suite',
        targetScope: scope,
        targetIds: scope === 'Customers' ? ids : [],
        forceSameVersion: !!releaseForm.forceSameVersion,
      })
      const releaseId = created?.id || created?.data?.id || created?.Id || created?.data?.Id
      if (releaseId && filesToUpload.length > 0) {
        let done = 0
        for (const item of filesToUpload) {
          await systemUpdateService.uploadPackage(String(releaseId), item.file, item.product, (p) => {
            const overall = Math.round(((done + p / 100) / filesToUpload.length) * 100)
            setUploadPercent(overall)
          })
          done += 1
          setUploadPercent(Math.round((done / filesToUpload.length) * 100))
        }
      }
      setReleaseFeedback(
        filesToUpload.length
          ? `تم نشر الإصدار ورفع ${filesToUpload.length} حزمة/حزم.`
          : 'تم نشر الإصدار. ارفع الحزم الأربع من قائمة الإصدارات أو أعد النشر مع الملفات.',
      )
      setReleaseForm({
        version: releaseForm.version.trim(),
        title: '',
        notes: '',
        isMandatory: true,
        product: 'Suite',
        targetScope: 'All',
        targetIds: [],
        forceSameVersion: false,
      })
      setSelectedCustomers([])
      setPackageFiles({ WinApp: null, Api: null, Mobile: null, MeraApp: null })
      setUploadPercent(null)
      await fetchReleases()
    } catch (err: any) {
      setReleaseFeedback(err?.response?.data?.message || 'تعذر نشر الإصدار الجديد')
    } finally {
      setReleaseSaving(false)
    }
  }

  const handleDeleteRelease = async (release: SystemUpdateInfo) => {
    if (!window.confirm(`هل تريد حذف الإصدار ${release.latestVersion}؟`)) return

    try {
      setReleaseDeletingId(release.id)
      setReleaseFeedback(null)
      await systemUpdateService.deleteRelease(release.id)
      setReleaseFeedback(`تم حذف الإصدار ${release.latestVersion} بنجاح.`)
      await fetchReleases()
    } catch (err: any) {
      setReleaseFeedback(err?.response?.data?.message || 'تعذر حذف الإصدار')
    } finally {
      setReleaseDeletingId(null)
    }
  }

  const activeRate = useMemo(() => {
    if (!stats?.totalDevices) return 0
    return Math.round((stats.activeDevices / stats.totalDevices) * 100)
  }, [stats])

  const resolvedRate = useMemo(() => {
    if (!stats?.totalErrors) return 0
    return Math.round((stats.fixedErrors / stats.totalErrors) * 100)
  }, [stats])

  const hasRecentErrors = (stats?.recentErrors?.length ?? 0) > 0
  const hasRecentVisits = (stats?.recentVisits?.length ?? 0) > 0
  const hasTopCustomers = (stats?.topCustomers?.length ?? 0) > 0

  return (
    <Fade in timeout={500}>
      <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
        <Paper
          sx={{
            ...glassCardSx,
            p: { xs: 2.5, md: 4 },
            mb: 4,
            overflow: 'hidden',
            position: 'relative',
            background:
              'linear-gradient(135deg, rgba(25,118,210,0.12), rgba(124,77,255,0.10), rgba(255,255,255,0.96))',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              insetInlineEnd: -80,
              insetBlockStart: -60,
              width: 220,
              height: 220,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(25,118,210,0.16) 0%, rgba(25,118,210,0) 70%)',
            }}
          />
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={3}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', lg: 'center' }}
            sx={{ position: 'relative', zIndex: 1 }}
          >
            <Box>
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight: 900,
                  mb: 1,
                  background: 'linear-gradient(135deg, #0f172a, #1976d2)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                لوحة التحكم التنفيذية
              </Typography>
              <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1.5, fontWeight: 500 }}>
                متابعة فورية للأجهزة والعملاء والزيارات وسجل الأخطاء من مكان واحد.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap">
                <Chip label={`المستخدم: ${user?.fullName || 'غير معروف'}`} color="primary" variant="outlined" />
                <Chip
                  label={`آخر تحديث: ${formatDateTime(stats?.lastUpdatedAt)}`}
                  color="default"
                  variant="outlined"
                />
                <Chip
                  label={`نسبة تفعيل الأجهزة: ${activeRate}%`}
                  color={activeRate >= 70 ? 'success' : 'warning'}
                />
              </Stack>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button
                variant="outlined"
                startIcon={<Assignment />}
                onClick={() => navigate('/service-visits')}
                sx={{ borderRadius: 3, px: 2.5 }}
              >
                زيارات الخدمة
              </Button>
              <Button
                variant="outlined"
                startIcon={<Groups />}
                onClick={() => navigate('/customers')}
                sx={{ borderRadius: 3, px: 2.5 }}
              >
                العملاء
              </Button>
              <Button
                variant="outlined"
                startIcon={<BugReport />}
                onClick={() => navigate('/errors')}
                sx={{ borderRadius: 3, px: 2.5 }}
              >
                إدارة الأخطاء
              </Button>
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={fetchDashboardStats}
                disabled={loading}
                sx={{
                  borderRadius: 3,
                  px: 2.5,
                  background: 'linear-gradient(135deg, #1976d2, #42a5f5)',
                  boxShadow: '0 10px 24px rgba(25,118,210,0.22)',
                }}
              >
                تحديث البيانات
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {loading && <LinearProgress sx={{ mb: 3, borderRadius: 999 }} />}

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} xl={2}>
            <StatCard
              title="إجمالي الأجهزة"
              value={stats?.totalDevices ?? 0}
              subtitle="كل الأجهزة المسجلة داخل النظام"
              icon={<Devices />}
              color="#1976d2"
              onClick={() => navigate('/devices')}
            />
          </Grid>
          <Grid item xs={12} sm={6} xl={2}>
            <StatCard
              title="الأجهزة النشطة"
              value={stats?.activeDevices ?? 0}
              subtitle={`${activeRate}% من إجمالي الأجهزة`}
              icon={<CheckCircle />}
              color="#2e7d32"
              onClick={() => navigate('/devices')}
            />
          </Grid>
          <Grid item xs={12} sm={6} xl={2}>
            <StatCard
              title="العملاء"
              value={stats?.totalCustomers ?? 0}
              subtitle="إجمالي العملاء المرتبطين بالنظام"
              icon={<Groups />}
              color="#8e24aa"
              onClick={() => navigate('/customers')}
            />
          </Grid>
          <Grid item xs={12} sm={6} xl={2}>
            <StatCard
              title="زيارات الشهر"
              value={stats?.totalVisitsThisMonth ?? 0}
              subtitle="عدد زيارات الخدمة خلال الشهر الحالي"
              icon={<Assignment />}
              color="#ef6c00"
              onClick={() => navigate('/service-visits')}
            />
          </Grid>
          <Grid item xs={12} sm={6} xl={2}>
            <StatCard
              title="الأخطاء المفتوحة"
              value={stats?.openErrors ?? 0}
              subtitle={`${stats?.reportedErrors ?? 0} مبلغ عنها و${stats?.unlinkedErrors ?? 0} بدون ربط`}
              icon={<WarningAmber />}
              color="#d32f2f"
              onClick={() => navigate('/errors')}
            />
          </Grid>
          <Grid item xs={12} sm={6} xl={2}>
            <StatCard
              title="الأخطاء المعالجة"
              value={stats?.fixedErrors ?? 0}
              subtitle={`${resolvedRate}% نسبة المعالجة الحالية`}
              icon={<TaskAlt />}
              color="#00897b"
              onClick={() => navigate('/errors')}
            />
          </Grid>
        </Grid>

        <Paper sx={{ ...glassCardSx, p: 3, mb: 4 }}>
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={3}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', lg: 'center' }}
            sx={{ mb: 2.5 }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
                تنبيهات الاشتراكات
              </Typography>
              <Typography variant="body2" color="text.secondary">
                متابعة الأجهزة القريبة من الانتهاء أو داخل فترة السماح أو التي تنتظر التفعيل الأول.
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} flexWrap="wrap">
              <Chip label={`قارب على الانتهاء: ${stats?.subscriptionOverview?.expiringSoon ?? 0}`} color="warning" />
              <Chip label={`فترة سماح: ${stats?.subscriptionOverview?.gracePeriod ?? 0}`} color="info" />
              <Chip label={`منتهي: ${stats?.subscriptionOverview?.expired ?? 0}`} color="error" />
              <Chip label={`بانتظار التفعيل: ${stats?.subscriptionOverview?.pendingActivation ?? 0}`} variant="outlined" />
              <Chip label={`دائمي: ${stats?.subscriptionOverview?.permanent ?? 0}`} color="success" variant="outlined" />
            </Stack>
          </Stack>

          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <StatCard title="نشط" value={stats?.subscriptionOverview?.active ?? 0} subtitle="اشتراكات فعالة" icon={<CheckCircle />} color="#2e7d32" />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <StatCard title="قريب" value={stats?.subscriptionOverview?.expiringSoon ?? 0} subtitle="ستنتهي قريباً" icon={<WarningAmber />} color="#ed6c02" />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <StatCard title="سماح" value={stats?.subscriptionOverview?.gracePeriod ?? 0} subtitle="داخل المهلة" icon={<Refresh />} color="#0288d1" />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <StatCard title="منتهي" value={stats?.subscriptionOverview?.expired ?? 0} subtitle="تحتاج تجديد" icon={<ErrorOutline />} color="#d32f2f" />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <StatCard title="بانتظار" value={stats?.subscriptionOverview?.pendingActivation ?? 0} subtitle="لم يبدأ بعد" icon={<Devices />} color="#6d4c41" />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <StatCard title="دائمي" value={stats?.subscriptionOverview?.permanent ?? 0} subtitle="بدون انتهاء" icon={<TaskAlt />} color="#7b1fa2" />
            </Grid>
          </Grid>

          {(stats?.subscriptionAlerts?.length ?? 0) === 0 ? (
            <Alert severity="success" sx={{ borderRadius: 3 }}>
              لا توجد اشتراكات حرجة حالياً، والأجهزة المستهدفة مستقرة.
            </Alert>
          ) : (
            <TableContainer>
              <Table sx={{ minWidth: 980 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800 }}>العميل</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>الجهاز</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>نوع الاشتراك</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>الحالة</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>الانتهاء</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>التنبيه</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats?.subscriptionAlerts?.map((alert: DashboardSubscriptionAlert) => (
                    <TableRow key={alert.deviceId} hover>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{alert.customerName}</Typography>
                          <Typography variant="caption" color="text.secondary">{alert.customerId}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{alert.deviceCode}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {alert.deviceType === 'Main'
                              ? 'جهاز رئيسي'
                              : alert.deviceType === 'Sub'
                                ? `جهاز فرعي${alert.mainDeviceCode ? ` / الرئيسي: ${alert.mainDeviceCode}` : ''}`
                                : alert.deviceType}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip label={getSubscriptionTypeLabel(alert.subscriptionType)} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getSubscriptionStateLabel(alert.subscriptionState)}
                          size="small"
                          color={getSubscriptionStateColor(alert.subscriptionState) as any}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatDateTime(alert.subscriptionEndsAt || alert.gracePeriodEndsAt)}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Typography variant="body2">{alert.alertMessage}</Typography>
                        {typeof alert.daysUntilExpiry === 'number' && (
                          <Typography variant="caption" color="text.secondary">
                            المتبقي: {alert.daysUntilExpiry} يوم
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {user?.role === 'Owner' && (
          <Paper sx={{ ...glassCardSx, p: 3, mb: 4 }}>
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3}>
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                  <SystemUpdateAlt color="primary" />
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>
                    إدارة تحديثات النظام
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  انشر إصداراً بأربع حزم (API + حاسبة + APK مندوبين + APK ميرا). للعملاء المحددين تظهر قائمة أجهزة أخضر/أحمر مع تأشير الـ API يدوياً.
                </Typography>
              </Box>

              <Stack spacing={2} sx={{ flex: 1.4 }}>
                {releaseFeedback && (
                  <Alert
                    severity={
                      releaseFeedback.includes('بنجاح') ||
                      releaseFeedback.includes('تم نشر') ||
                      releaseFeedback.includes('تم حذف')
                        ? 'success'
                        : 'error'
                    }
                    sx={{ borderRadius: 3 }}
                  >
                    {releaseFeedback}
                  </Alert>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="رقم الإصدار"
                      value={releaseForm.version}
                      onChange={(event) => setReleaseForm((prev) => ({ ...prev, version: event.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      label="عنوان التحديث"
                      value={releaseForm.title}
                      onChange={(event) => setReleaseForm((prev) => ({ ...prev, title: event.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="ملاحظات التحديث"
                      multiline
                      minRows={2}
                      value={releaseForm.notes || ''}
                      onChange={(event) => setReleaseForm((prev) => ({ ...prev, notes: event.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>المنتج</InputLabel>
                      <Select
                        label="المنتج"
                        value={releaseForm.product || 'Suite'}
                        onChange={(event) =>
                          setReleaseForm((prev) => ({
                            ...prev,
                            product: event.target.value as CreateSystemReleasePayload['product'],
                          }))
                        }
                      >
                        <MenuItem value="Suite">الكل (4 حزم)</MenuItem>
                        <MenuItem value="Both">WinApp + API</MenuItem>
                        <MenuItem value="WinApp">WinApp فقط</MenuItem>
                        <MenuItem value="Api">API فقط</MenuItem>
                        <MenuItem value="Mobile">مندوبين فقط</MenuItem>
                        <MenuItem value="MeraApp">ميرا فقط</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>نطاق التوزيع</InputLabel>
                      <Select
                        label="نطاق التوزيع"
                        value={releaseForm.targetScope || 'All'}
                        onChange={(event) =>
                          setReleaseForm((prev) => ({
                            ...prev,
                            targetScope: event.target.value as CreateSystemReleasePayload['targetScope'],
                          }))
                        }
                      >
                        <MenuItem value="All">الكل</MenuItem>
                        <MenuItem value="Customers">عملاء محددون</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {releaseForm.targetScope === 'Customers' && (
                    <Grid item xs={12}>
                      <Autocomplete
                        multiple
                        options={customerOptions}
                        value={selectedCustomers}
                        onChange={(_, value) => setSelectedCustomers(value)}
                        getOptionLabel={(option) => option.name || option.id}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        renderInput={(params) => (
                          <TextField {...params} label="اختر العملاء" placeholder="بحث باسم العميل..." />
                        )}
                      />
                    </Grid>
                  )}
                  {PACKAGE_SLOTS.map((slot) => (
                    <Grid item xs={12} md={6} key={slot.product}>
                      <Button
                        component="label"
                        fullWidth
                        variant="outlined"
                        startIcon={<CloudUpload />}
                        sx={{ borderRadius: 2, justifyContent: 'flex-start', textAlign: 'start' }}
                      >
                        {packageFiles[slot.product]?.name || slot.label}
                        <input
                          type="file"
                          hidden
                          accept={slot.accept}
                          onChange={(event) =>
                            setPackageFiles((prev) => ({
                              ...prev,
                              [slot.product]: event.target.files?.[0] || null,
                            }))
                          }
                        />
                      </Button>
                    </Grid>
                  ))}
                  {uploadPercent !== null && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        رفع الحزم: {uploadPercent}%
                      </Typography>
                      <LinearProgress variant="determinate" value={uploadPercent} sx={{ mt: 0.5, borderRadius: 999 }} />
                    </Grid>
                  )}
                </Grid>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" flexWrap="wrap">
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={releaseForm.isMandatory}
                          onChange={(event) => setReleaseForm((prev) => ({ ...prev, isMandatory: event.target.checked }))}
                        />
                      }
                      label="تحديث إجباري"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!releaseForm.forceSameVersion}
                          onChange={(event) =>
                            setReleaseForm((prev) => ({ ...prev, forceSameVersion: event.target.checked }))
                          }
                        />
                      }
                      label="فرض بنفس الإصدار"
                    />
                  </Stack>
                  <Button
                    variant="contained"
                    startIcon={releaseSaving ? <CircularProgress size={18} color="inherit" /> : <CloudUpload />}
                    onClick={handleCreateRelease}
                    disabled={releaseSaving}
                    sx={{ borderRadius: 3, px: 3 }}
                  >
                    {releaseSaving ? 'جاري النشر...' : 'نشر الإصدار'}
                  </Button>
                </Stack>

                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  الإصدارات المنشورة ({releases.length})
                </Typography>
                {releases.length === 0 ? (
                  <Alert severity="info" sx={{ borderRadius: 3 }}>
                    لا توجد إصدارات بعد. انشر إصداراً جديداً من النموذج أعلاه.
                  </Alert>
                ) : (
                  <Stack spacing={1.25}>
                    {releases.map((release) => (
                      <Paper
                        key={release.id}
                        variant="outlined"
                        sx={{ p: 1.75, borderRadius: 3, bgcolor: 'rgba(25,118,210,0.03)' }}
                      >
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1.5}
                          justifyContent="space-between"
                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                        >
                          <Box>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Typography sx={{ fontWeight: 800 }}>v{release.latestVersion}</Typography>
                              <Chip size="small" label={release.title} color="primary" variant="outlined" />
                              <Chip size="small" label={release.product || 'Both'} variant="outlined" />
                              <Chip size="small" label={release.targetScope || 'All'} variant="outlined" />
                              {(release.packages || []).map((pkg) => (
                                <Chip
                                  key={pkg.product}
                                  size="small"
                                  label={pkg.product}
                                  color={pkg.hasPackage ? 'success' : 'default'}
                                  variant={pkg.hasPackage ? 'filled' : 'outlined'}
                                />
                              ))}
                              {release.forceSameVersion && <Chip size="small" label="فرض" color="info" />}
                              {release.isMandatory && <Chip size="small" label="إجباري" color="warning" />}
                              {(release.enrolledCount ?? 0) > 0 && (
                                <Chip
                                  size="small"
                                  label={`${release.updatedCount ?? 0}/${release.enrolledCount} محدّث`}
                                  color={(release.pendingCount ?? 0) === 0 ? 'success' : 'error'}
                                />
                              )}
                            </Stack>
                            {release.notes && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                {release.notes}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {formatDateTime(release.createdAt)} — {release.createdByName || '—'}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={1}>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => loadBoard(release)}
                              sx={{ borderRadius: 2 }}
                            >
                              الأجهزة
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              startIcon={<Delete />}
                              disabled={releaseDeletingId === release.id}
                              onClick={() => handleDeleteRelease(release)}
                              sx={{ borderRadius: 2 }}
                            >
                              {releaseDeletingId === release.id ? '...' : 'حذف'}
                            </Button>
                          </Stack>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Stack>

            <Dialog open={boardOpen} onClose={() => setBoardOpen(false)} fullWidth maxWidth="md">
              <DialogTitle>
                حالة تحديث v{boardRelease?.latestVersion} — أخضر محدّث / أحمر لم يحدّث
              </DialogTitle>
              <DialogContent dividers>
                <Stack spacing={2} sx={{ mb: 2 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                    <Autocomplete
                      sx={{ flex: 1, minWidth: 220 }}
                      options={customerOptions}
                      value={addCustomerPick}
                      onChange={(_, value) => setAddCustomerPick(value)}
                      getOptionLabel={(option) => option.name || option.id}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      renderInput={(params) => (
                        <TextField {...params} size="small" label="إضافة وكيل/عميل آخر" />
                      )}
                    />
                    <Button
                      variant="outlined"
                      disabled={!addCustomerPick || !boardRelease}
                      onClick={async () => {
                        if (!boardRelease || !addCustomerPick) return
                        try {
                          setBoardLoading(true)
                          const response = await systemUpdateService.addCustomers(boardRelease.id, [
                            addCustomerPick.id,
                          ])
                          const payload = response?.data ?? response?.Data ?? response
                          setBoard(payload as ReleaseEnrollmentBoard)
                          setAddCustomerPick(null)
                          await fetchReleases()
                        } catch (err: any) {
                          setReleaseFeedback(err?.response?.data?.message || 'تعذر إضافة العميل')
                        } finally {
                          setBoardLoading(false)
                        }
                      }}
                    >
                      إضافة أجهزته
                    </Button>
                  </Stack>
                  <TextField
                    size="small"
                    label="بحث بالعميل / الملاحظات / رقم الجهاز / التفعيل"
                    placeholder="مثال: سامر العاني"
                    value={boardQuery}
                    onChange={(e) => setBoardQuery(e.target.value)}
                    fullWidth
                  />
                  {board && (
                    <Typography variant="body2" color="text.secondary">
                      {board.updated} محدّث / {board.pending} متبقّي من أصل {board.total}
                      {boardQuery.trim()
                        ? ` — ظاهر ${filteredBoardDevices.length} بعد البحث`
                        : ''}
                    </Typography>
                  )}
                </Stack>
                {boardLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : !board?.devices?.length ? (
                  <Alert severity="info">لا أجهزة مسجّلة في هذه الحملة بعد.</Alert>
                ) : !filteredBoardDevices.length ? (
                  <Alert severity="info">لا نتائج لـ «{boardQuery}». جرّب اسم العميل كما في البطاقة.</Alert>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>الحالة</TableCell>
                          <TableCell>العميل</TableCell>
                          <TableCell>النوع</TableCell>
                          <TableCell>الجهاز</TableCell>
                          <TableCell>الإصدار</TableCell>
                          <TableCell>من حدّث</TableCell>
                          <TableCell align="left">إجراء</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredBoardDevices.map((row) => (
                          <TableRow
                            key={row.deviceId}
                            sx={{
                              bgcolor: row.isUpdated
                                ? 'rgba(46,125,50,0.08)'
                                : 'rgba(211,47,47,0.08)',
                            }}
                          >
                            <TableCell>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <Chip
                                  size="small"
                                  color={row.isUpdated ? 'success' : 'error'}
                                  label={row.isUpdated ? 'محدّث' : 'لم يحدّث'}
                                />
                                {row.manuallyMarkedUpdated && (
                                  <Chip size="small" label="يدوي" color="info" />
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>{row.customerName}</TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <span>{boardDeviceTypeLabel(row.deviceType)}</span>
                                {row.deviceType === 'Api' && (
                                  <Chip
                                    size="small"
                                    label={row.apiHostingKind === 'Host' ? 'هوست' : 'ويندوز'}
                                    color={row.apiHostingKind === 'Host' ? 'warning' : 'default'}
                                    variant="outlined"
                                  />
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              {row.deviceType === 'Api' ? (
                                <>
                                  <div>رقم الرئيسية: {row.mainComputerName || '—'}</div>
                                  <div>تفعيل الرئيسية: {row.mainActivationCode || '—'}</div>
                                  <div>تفعيل API: {row.activationCode || '—'}</div>
                                </>
                              ) : (
                                <>
                                  <div>{row.computerName || row.deviceCode}</div>
                                  {row.activationCode ? <div>تفعيل: {row.activationCode}</div> : null}
                                </>
                              )}
                            </TableCell>
                            <TableCell>{row.currentVersion || '—'}</TableCell>
                            <TableCell>
                              {row.markedUpdatedByName || row.lastUpdatedByName || '—'}
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.5}>
                                {!row.isUpdated && (
                                  <Button
                                    size="small"
                                    startIcon={<DoneAll />}
                                    onClick={async () => {
                                      if (!boardRelease) return
                                      try {
                                        await systemUpdateService.markDeviceUpdated(
                                          boardRelease.id,
                                          row.deviceId,
                                        )
                                        await loadBoard(boardRelease)
                                        await fetchReleases()
                                      } catch (err: any) {
                                        setReleaseFeedback(
                                          err?.response?.data?.message || 'تعذر التأشير',
                                        )
                                      }
                                    }}
                                  >
                                    {row.deviceType === 'Api' && row.apiHostingKind === 'Host'
                                      ? 'تأشير بعد الرفع اليدوي'
                                      : 'تأشير محدّث'}
                                  </Button>
                                )}
                                {!row.isUpdated && (
                                  <Button
                                    size="small"
                                    color="error"
                                    startIcon={<Delete />}
                                    onClick={async () => {
                                      if (!boardRelease) return
                                      if (!window.confirm('حذف هذا الجهاز الأحمر من الحملة؟')) return
                                      try {
                                        await systemUpdateService.removeEnrollment(
                                          boardRelease.id,
                                          row.deviceId,
                                        )
                                        await loadBoard(boardRelease)
                                        await fetchReleases()
                                      } catch (err: any) {
                                        setReleaseFeedback(
                                          err?.response?.data?.message || 'تعذر الحذف',
                                        )
                                      }
                                    }}
                                  >
                                    حذف
                                  </Button>
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setBoardOpen(false)}>إغلاق</Button>
              </DialogActions>
            </Dialog>
          </Paper>
        )}

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} lg={7}>
            <Paper sx={{ ...glassCardSx, p: 3, height: '100%' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
                    أحدث زيارات الخدمة
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    آخر النشاطات الميدانية المسجلة للفنيين والعملاء.
                  </Typography>
                </Box>
                <Chip label={`${stats?.recentVisits?.length ?? 0} زيارة`} color="primary" variant="outlined" />
              </Stack>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : !hasRecentVisits ? (
                <Alert severity="info" sx={{ borderRadius: 3 }}>
                  لا توجد زيارات حديثة لعرضها حالياً.
                </Alert>
              ) : (
                <List sx={{ p: 0 }}>
                  {stats?.recentVisits.map((visit: DashboardRecentVisit, index) => (
                    <React.Fragment key={visit.id}>
                      <ListItem sx={{ px: 0, py: 1.75, alignItems: 'flex-start' }}>
                        <Avatar
                          variant="rounded"
                          sx={{
                            ml: 2,
                            mt: 0.25,
                            bgcolor: alpha('#1976d2', 0.12),
                            color: '#1976d2',
                            borderRadius: 3,
                          }}
                        >
                          <Assignment />
                        </Avatar>
                        <ListItemText
                          primary={
                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={1}
                              alignItems={{ xs: 'flex-start', sm: 'center' }}
                              sx={{ mb: 0.75 }}
                            >
                              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                {visit.customerName}
                              </Typography>
                              <Chip label={getVisitTypeLabel(visit.visitType)} size="small" color="info" />
                              <Chip label={visit.deviceCode} size="small" variant="outlined" />
                            </Stack>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                الفني: {visit.technicianName}
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                {visit.description}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                                {formatDateTime(visit.visitDate)}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < (stats?.recentVisits.length ?? 0) - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Paper sx={{ ...glassCardSx, p: 3, height: '100%' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
                    العملاء الأكثر تأثيراً
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ترتيب مبني على كثافة الأخطاء ثم عدد الأجهزة المرتبطة.
                  </Typography>
                </Box>
                <Insights color="primary" />
              </Stack>

              {!hasTopCustomers ? (
                <Alert severity="info" sx={{ borderRadius: 3 }}>
                  لا توجد بيانات كافية لعرض ترتيب العملاء حالياً.
                </Alert>
              ) : (
                <Stack spacing={2}>
                  {stats?.topCustomers.map((customer, index) => (
                    <Paper
                      key={customer.customerId}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        backgroundColor: alpha('#1976d2', index === 0 ? 0.08 : 0.04),
                        border: `1px solid ${alpha('#1976d2', 0.12)}`,
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar sx={{ bgcolor: alpha('#8e24aa', 0.14), color: '#8e24aa' }}>
                            <Person />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                              {customer.customerName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {customer.location}
                            </Typography>
                          </Box>
                        </Stack>
                        <Stack alignItems="flex-end" spacing={0.75}>
                          <Chip label={`${customer.errorCount} خطأ`} color="error" size="small" />
                          <Chip label={`${customer.deviceCount} جهاز`} variant="outlined" size="small" />
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Paper>
          </Grid>
        </Grid>

        <Paper sx={{ ...glassCardSx, p: 3 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2}
            sx={{ mb: 2.5 }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
                جدول الأخطاء
              </Typography>
              <Typography variant="body2" color="text.secondary">
                سجل احترافي لآخر الأخطاء مع ربط العميل وحالة المعالجة والتبليغ.
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              <Chip label={`إجمالي الأخطاء: ${stats?.totalErrors ?? 0}`} color="default" variant="outlined" />
              <Chip label={`الأخطاء المفتوحة: ${stats?.openErrors ?? 0}`} color="error" />
              <Chip label={`المعالجة: ${stats?.fixedErrors ?? 0}`} color="success" />
            </Stack>
          </Stack>

          {!hasRecentErrors ? (
            <Alert severity="success" sx={{ borderRadius: 3 }}>
              لا توجد أخطاء حديثة حالياً، وهذا مؤشر جيد على استقرار النظام.
            </Alert>
          ) : (
            <TableContainer>
              <Table sx={{ minWidth: 1100 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800 }}>الوقت</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>العميل</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>المصدر</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>النوع</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>الرسالة</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>المسار / الشاشة</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>الجهاز</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>الحالة</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>التبليغ</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>الإجراءات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats?.recentErrors.map((errorRow: DashboardErrorLog) => (
                    <TableRow
                      key={errorRow.errorId}
                      hover
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        backgroundColor: errorRow.isFixed ? alpha('#2e7d32', 0.02) : alpha('#d32f2f', 0.02),
                      }}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(errorRow.errorTime)}</TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {errorRow.customerName || 'غير مربوط'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {errorRow.userName || 'بدون مستخدم'}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip label={errorRow.source} size="small" color={getErrorSourceColor(errorRow.source) as any} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{errorRow.errorType || 'غير محدد'}</Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 340 }}>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          {errorRow.isFixed ? (
                            <CheckCircle sx={{ color: '#2e7d32', fontSize: 18, mt: 0.2 }} />
                          ) : (
                            <BugReport sx={{ color: '#d32f2f', fontSize: 18, mt: 0.2 }} />
                          )}
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {errorRow.errorMessage}
                            </Typography>
                            {errorRow.notes && (
                              <Typography variant="caption" color="text.secondary">
                                ملاحظة: {errorRow.notes}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2">{errorRow.formOrEndpoint || errorRow.requestPath || '-'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {errorRow.httpMethod || ''} {errorRow.methodName || ''}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {errorRow.deviceCode || errorRow.computerNo || '-'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {errorRow.deviceType === 'Main'
                              ? 'جهاز رئيسي'
                              : errorRow.deviceType === 'Sub'
                                ? `جهاز فرعي${errorRow.mainDeviceCode ? ` / الرئيسي: ${errorRow.mainDeviceCode}` : ''}`
                                : 'غير مربوط'}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {errorRow.isFixed ? (
                          <Chip
                            size="small"
                            color="success"
                            label={errorRow.fixedBy ? `تم الإصلاح بواسطة ${errorRow.fixedBy}` : 'تم الإصلاح'}
                          />
                        ) : (
                          <Chip size="small" color="error" label="مفتوح" />
                        )}
                      </TableCell>
                      <TableCell>
                        {errorRow.isReportedToHaizoom ? (
                          <Chip
                            size="small"
                            color="info"
                            label={errorRow.reportedBy ? `أبلغ: ${errorRow.reportedBy}` : 'تم التبليغ'}
                          />
                        ) : (
                          <Chip size="small" variant="outlined" icon={<ErrorOutline />} label="غير مبلغ" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            disabled={errorRow.isFixed || errorActionId === errorRow.errorId}
                            onClick={() => handleFixError(errorRow.errorId)}
                            sx={{ borderRadius: 2 }}
                          >
                            {errorActionId === errorRow.errorId ? '...' : 'تم الإصلاح'}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="info"
                            disabled={errorRow.isReportedToHaizoom || errorActionId === errorRow.errorId}
                            onClick={() => handleReportError(errorRow.errorId)}
                            sx={{ borderRadius: 2 }}
                          >
                            تبليغ
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>
    </Fade>
  )
}
